import { useQuery, UseQueryOptions, UseQueryResult } from '@taxdown/zeno-sama-query';
import Notify, { AlertTypes } from 'Services/notification';
import SatService from 'Services/sat';
import { PueblaPreviousYearsResponse, SatResponseType } from 'Services/sat/types';
import i18n from 'Services/i18n';
import StorageService from 'Services/storage';
import { history } from 'Store';
import { PreviousYearStatus } from 'Containers/PreviousYears/types';
import { stages } from 'Constants/stages';
import { routes } from 'Constants/routes';
import { ErrorResponseType, QueryKeys, ReintentType, ResponseType } from 'Types/common';

export const downloadDataAnualDeclaration = (
  data?: ResponseType<SatResponseType>
): number | false => {
  const response = data?.data;
  if (!response) {
    return 500;
  }
  const redirectStages = [stages.DATA_PARTIAL_PULLED, stages.DATA_PULLED];

  if (redirectStages.includes(response.item?.stage?.value)) {
    history.push(routes.PERSONAL_START);
    return false;
  }
  if (response.item.stage?.value === stages.DATA_ERROR) {
    history.push(routes.DATAGATHERING_ERROR);
    return false;
  }
  if (response.item.stage?.value === (stages.VALIDATING_CREDENTIALS_ERROR || stages.EXPIRED)) {
    const stage = response.item.stage?.value;
    history.push(routes.DATAGATHERING_IDENTIFICATION);
    Notify.sendNotification(
      stage === stages.EXPIRED
        ? i18n.t('dg:DG.Loader.passworderror')
        : i18n.t('dg:DG.Loader.rfcerror'),
      AlertTypes.error
    );
    return false;
  }
  return 500;
};

export const useFetchPollingAnualDeclaration = (
  isAnualDeclaration?: boolean
): UseQueryResult<ResponseType<SatResponseType>> =>
  useQuery([QueryKeys.ANUAL_DECLARATION], () => SatService.getSatUser(), {
    refetchOnWindowFocus: false,
    onError: error => {
      const errorData = error as ErrorResponseType;
      const errorDetail = errorData?.response?.data?.detail;
      Notify.sendNotification(errorDetail, AlertTypes.error);
    },
    refetchInterval: data => downloadDataAnualDeclaration(data),
    cacheTime: 0,
    enabled: isAnualDeclaration,
  });

export const useFetchPollingPreviousYears = (
  isPreviousYear?: boolean
): UseQueryResult<ResponseType<PueblaPreviousYearsResponse>> =>
  useQuery([QueryKeys.PREVIOUS_YEARS], SatService.getPueblaPreviousYears, {
    onSuccess: data => {
      const response = data.data;
      if (
        response?.item?.records?.length > 0 &&
        response?.item?.stage === PreviousYearStatus.READY
      ) {
        StorageService.removeItem('isPreviousYear');
        history.push(routes.PREVIOUS_YEARS);
      }
    },
    onError: error => {
      const errorData = error as ErrorResponseType;
      const errorDetail = errorData?.response?.data?.detail;
      Notify.sendNotification(errorDetail, AlertTypes.error);
    },
    refetchInterval: data => {
      const response = data?.data;
      if (!response?.item) {
        return 500;
      }
      if (response?.item && response?.item?.stage === PreviousYearStatus.CALCULATING) {
        return 500;
      }
      return false;
    },
    cacheTime: 0,
    enabled: isPreviousYear,
    refetchOnWindowFocus: false,
  });

export const useFetchReintents = (
  options?: UseQueryOptions<ResponseType<ReintentType>>
): UseQueryResult<ResponseType<ReintentType>> =>
  useQuery([QueryKeys.REINTENTS], SatService.getReintents, options);
